import { AdContainer } from '@components/common/AdContainer/AdContainer';
import { Breadcrumb } from '@components/common/Breadcrumb';
import { CoinTicker } from '@components/common/CoinTicker';
import { SidebarWrapper } from '@components/common/SidebarWrapper/SidebarWrapper';
import { Tooltip } from '@components/common/Tooltip';
import { MEDIA_QUERIES, P, useMediaQuery } from '@student-coin/coinpaper-components';
import { getArticleDates } from '@utils/getArticleDates';
import { buildAuthorPageLink } from '@utils/link';
import useTranslation from 'next-translate/useTranslation';
import dynamic from 'next/dist/shared/lib/dynamic';
import NextLink from 'next/link';
import { ReactElement } from 'react';
import { Sponsored } from '../Sponsored/Sponsored';
import { ArticleAudio } from './ArticleAudio';
import {
  Container,
  DateContainer,
  Footer,
  H1,
  Info,
  Label,
  Meta,
  UpdatedAtIcon,
} from './ArticleHeader.styles';
import { ArticleHeaderProps } from './ArticleHeader.types';

const AdPlacementComponent = dynamic(
  () => import('@components/common/AdPlacement').then((mod) => mod.AdPlacement),
  { ssr: false },
);

export const ArticleHeader = ({
  author,
  title,
  description,
  updatedAt,
  publishedDate,
  publishedAt,
  category,
  subcategory,
  sponsored,
  readTimeInMinutes,
  articleAudioId,
  ads,
  assets,
}: ArticleHeaderProps): ReactElement => {
  const { t } = useTranslation('common');
  const isLaptopWide = useMediaQuery(MEDIA_QUERIES.laptopL);
  const { updatedAt: updatedAtFormatted, publishedAt: publishedAtFormatted } = getArticleDates({
    publishedDate,
    publishedAt,
    updatedAt,
    dateFormat: 'd MMMM yyyy',
    fullTime: true,
  });

  return (
    <Container>
      <AdContainer adPlacements={ads} placement="classic-article-header-banner">
        <AdPlacementComponent adPlacements={ads} placement="classic-article-header-banner" />
      </AdContainer>

      <header>
        <Meta>
          <Breadcrumb subcategory={subcategory} category={category} />
          <Sponsored sponsored={sponsored} />
        </Meta>
        <H1>{title}</H1>
        <P variant="subtitle">{description}</P>

        <Footer>
          <Info>
            {author && (
              <NextLink href={buildAuthorPageLink(author.id, author.slug)}>
                <Label variant="large" style={{ cursor: 'pointer' }}>
                  {author.name}
                </Label>
              </NextLink>
            )}
            <Label variant="large">{`${readTimeInMinutes ?? 2} ${t('min-read')}`}</Label>
            <DateContainer>
              <Label as="span" variant="large">
                {publishedAtFormatted}
              </Label>
              {updatedAtFormatted && (
                <Tooltip text={updatedAtFormatted} id="updateDate">
                  <UpdatedAtIcon />
                </Tooltip>
              )}
            </DateContainer>
          </Info>

          {articleAudioId && <ArticleAudio audioId={articleAudioId} />}
          {!isLaptopWide && assets?.length > 0 && (
            <SidebarWrapper heading="In this article">
              {assets.map((asset) => {
                return <CoinTicker {...asset} />;
              })}
            </SidebarWrapper>
          )}
        </Footer>
      </header>
    </Container>
  );
};
